import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as CS from './Common.styles';
import * as S from './Invoices.styles';
import {
  Invoice,
  invoices,
  renderStatus,
} from '@/components/Profile/Business/Invoices.constants';
import { AngleBackIcon } from '@/components/UI/Icons/Icons';
import { formatNumber, sleep } from '@/utils/helpers';
import { Drawer } from '@/components/Drawer/Drawer';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { MODAL_ANIMATION_DURATION } from '@/styles/constants';

interface InvoicesProps {
  id: string;
}

export const Invoices = React.forwardRef<HTMLDivElement, InvoicesProps>(
  ({ id }, ref) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [isOpen, toggle] = useToggleOpenState();
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(
      null
    );

    async function closeInvoice() {
      toggle();
      await sleep(MODAL_ANIMATION_DURATION);
      setSelectedInvoice(null);
    }

    function openInvoice(invoice: Invoice) {
      setSelectedInvoice(invoice);
      toggle();
    }

    return (
      <CS.Box id={id} ref={ref}>
        <Typography.Header $variant="2">
          {t('pages.cabinet.invoices')}
        </Typography.Header>
        {invoices.length ? (
          <>
            {invoices.map(invoice => {
              const status = renderStatus(invoice.status, theme, t);

              return (
                <S.Invoice
                  key={invoice.id}
                  onClick={() => openInvoice(invoice)}
                >
                  <S.Row>
                    <div>
                      <S.PackageName $variant="1">
                        {invoice.packageName}
                      </S.PackageName>
                      <S.Number $variant="1">№ {invoice.number}</S.Number>
                    </div>
                    <div>
                      <S.Price $variant="1">
                        {formatNumber(invoice.price, 2)} AZN
                      </S.Price>
                      <S.Status $variant="1" $color={status.color}>
                        {status.label}
                      </S.Status>
                    </div>
                    <S.ImgBox>
                      <AngleBackIcon rotateAngle={180} />
                    </S.ImgBox>
                  </S.Row>

                  <S.DownloadButton appearance="secondaryText" size="s">
                    {t('common.download')}
                  </S.DownloadButton>
                </S.Invoice>
              );
            })}

            <Drawer
              isOpen={isOpen}
              close={closeInvoice}
              title={t('pages.cabinet.package_name', {
                name: selectedInvoice?.packageName,
              })}
              hasBottomMargin={false}
            >
              <S.PayDate $variant="1">{selectedInvoice?.paymentDate}</S.PayDate>
              <S.Table>
                <S.Row $is2Columns>
                  <S.Name $variant="1">
                    {t('pages.cabinet.paid_ads_colon')}
                  </S.Name>
                  <S.Value $variant="1">{selectedInvoice?.adsCount}</S.Value>
                </S.Row>

                <S.Row $is2Columns>
                  <S.Name $variant="1">
                    {t('pages.cabinet.paid_services_colon')}
                  </S.Name>
                  <S.Value $variant="1">{selectedInvoice?.price}</S.Value>
                </S.Row>

                <S.Row $is2Columns>
                  <S.Name $variant="1">
                    {t('pages.cabinet.discount_amount_colon')}
                  </S.Name>
                  <S.Value $variant="1">{selectedInvoice?.discount}%</S.Value>
                </S.Row>

                <S.Row $is2Columns>
                  <S.Name $variant="1">
                    {t('pages.cabinet.package_period_colon')}
                  </S.Name>
                  <S.Value $variant="1">
                    {selectedInvoice?.from} - {selectedInvoice?.to}
                  </S.Value>
                </S.Row>

                <S.Row $is2Columns $hasBorders>
                  <S.Name $variant="1">{t('pages.cabinet.vat_18')}</S.Name>
                  <S.Value $variant="1">{selectedInvoice?.vat}</S.Value>
                </S.Row>

                <S.Row $is2Columns $isLast>
                  <Typography.Title $variant="1">
                    {t('pages.cabinet.total_for_pay_colon')}
                  </Typography.Title>
                  <S.TotalValue $variant="1">
                    {selectedInvoice?.price} AZN
                  </S.TotalValue>
                </S.Row>
              </S.Table>
            </Drawer>
          </>
        ) : (
          <>
            <S.Text $variant="3">
              {t('pages.cabinet.you_dont_have_operations_yet')}
            </S.Text>
            <S.Subtitle $variant="1">
              {t('pages.cabinet.invoices_history_will_be_displayed_here')}
            </S.Subtitle>
          </>
        )}
      </CS.Box>
    );
  }
);

Invoices.displayName = 'Invoices';
